/**
 * Terrain Generation — archetype-driven procedural system.
 * 3 archetypes (Battlefield, Archipelago, Tower), wireframe + sand two-layer terrain.
 * Variable stage width within fixed 2560x1440 world.
 *
 * Pipeline: seed → archetype → stageBounds → wireframe layout → rasterize
 *           → sand heightmap → spawns → validation → cache
 */

import { WORLD, TERRAIN, TERRAIN_GEN, STAGE, ARCHETYPE, setWorldWidth, setWorldHeight } from '../constants';
import type { WireframeGround } from '../types';
import { createGrid, getGroundY, getGrid, rebuildColumnTopCache, rasterizeWireframe, worldToGridX, worldToGridY, importGridBase64 } from './grid';
import { initRNG, randomRange } from './rng';
import { selectArchetype, generateArchetypeLayout } from './archetypes';
import type { ArchetypeType } from './archetypes';
import { generateSandHeightmap } from './draping';
import { getStage } from '../stages';
import { getBakedPreset } from '../stages/presets';

const { CELL_SIZE } = TERRAIN;

// --- Stage bounds & spawn calculation ---

interface StageBounds {
  stageLeft: number;
  stageRight: number;
  stageWidth: number;
}

function calculateSpawnPositions(bounds: StageBounds, playerCount: number): number[] {
  const margin = bounds.stageWidth * STAGE.SPAWN_MARGIN_FRAC;
  const usable = bounds.stageWidth - 2 * margin;

  if (playerCount <= 1) {
    return [bounds.stageLeft + bounds.stageWidth / 2];
  }

  const spawnXs: number[] = [];
  for (let i = 0; i < playerCount; i++) {
    const t = i / (playerCount - 1); // 0..1
    spawnXs.push(Math.round(bounds.stageLeft + margin + t * usable));
  }
  return spawnXs;
}

function validatePlayability(spawnXs: number[]): void {
  const grid = getGrid();
  if (!grid) return;

  // Clear space above each spawn (spawnXs are world coords)
  for (const sx of spawnXs) {
    const gx = Math.floor(sx / CELL_SIZE);
    const groundY = getGroundY(sx);
    const groundGy = Math.floor(groundY / CELL_SIZE);
    const clearW = TERRAIN_GEN.SPAWN_CLEAR_RADIUS;
    const clearH = TERRAIN_GEN.SPAWN_CLEAR_HEIGHT;

    for (let dy = 1; dy <= clearH; dy++) {
      const gy = groundGy - dy;
      if (gy < 0) break;
      for (let dx = -clearW; dx <= clearW; dx++) {
        const cx = gx + dx;
        if (cx >= 0 && cx < TERRAIN.GRID_WIDTH && gy >= 0 && gy < TERRAIN.GRID_HEIGHT) {
          grid[gy * TERRAIN.GRID_WIDTH + cx] = 0;
        }
      }
    }
  }

  // Ensure no fully solid columns between spawns (carve passage)
  if (spawnXs.length >= 2) {
    const minGx = Math.floor(Math.min(...spawnXs) / CELL_SIZE);
    const maxGx = Math.floor(Math.max(...spawnXs) / CELL_SIZE);

    for (let gx = minGx; gx <= maxGx; gx++) {
      // Check if entire column is solid (blocked)
      let allSolid = true;
      for (let gy = 0; gy < TERRAIN.GRID_HEIGHT; gy++) {
        if (grid[gy * TERRAIN.GRID_WIDTH + gx] === 0) {
          allSolid = false;
          break;
        }
      }
      if (allSolid) {
        // Carve a passage at mid-height
        const midGy = Math.floor(TERRAIN.GRID_HEIGHT * 0.4);
        const passageH = 40; // ~80px
        for (let gy = midGy; gy < Math.min(TERRAIN.GRID_HEIGHT, midGy + passageH); gy++) {
          grid[gy * TERRAIN.GRID_WIDTH + gx] = 0;
        }
      }
    }
  }
}

// --- Public API ---

export interface TerrainResult {
  seed: number;
  biome: string;
  biomeDisplayName: string;
  feature: string | null;
  featureDisplayName: string | null;
  spawnXs: number[];
  spawnYs?: number[];    // editor Y coords — if present, used instead of getGroundY
  stageLeft: number;
  stageRight: number;
}

export function generateTerrain(playerCount: number, seed?: number, forcedArchetype?: ArchetypeType, stageId?: string): TerrainResult {
  // 1. Initialize seeded RNG
  const resolvedSeed = initRNG(seed);

  // Fixed stage path — use stage layout instead of archetype
  if (stageId) {
    // Baked preset path — hand-crafted level from level editor
    const baked = getBakedPreset(stageId);
    if (baked) {
      setWorldWidth(baked.worldWidth ?? 2560);
      createGrid();
      importGridBase64(baked.gridData);
      rebuildColumnTopCache();

      const spawnXs = baked.spawnXs.slice(0, playerCount);
      flattenSpawnsOnGrid(spawnXs);
      validatePlayability(spawnXs);
      rebuildColumnTopCache();

      const stage = getStage(stageId);
      console.log(`[TERRAIN] Baked: ${stage.theme.name} (seed: ${resolvedSeed.toString(36).toUpperCase()})`);

      return {
        seed: resolvedSeed,
        biome: stageId,
        biomeDisplayName: stage.theme.name,
        feature: null,
        featureDisplayName: null,
        spawnXs,
        spawnYs: baked.spawnYs?.slice(0, playerCount),
        stageLeft: baked.stageLeft,
        stageRight: baked.stageRight,
      };
    }

    // Procedural stage path (stages without baked presets)
    const stage = getStage(stageId);
    setWorldWidth(stage.worldWidth ?? 2560);
    setWorldHeight(stage.worldHeight ?? 1440);

    // Calculate stage bounds (use worldWidth for wide stages, standard for normal)
    const worldW = WORLD.VIRTUAL_WIDTH;
    const stageWidth = stage.worldWidth ? (worldW - 200) : Math.min(STAGE.MAX_WIDTH, Math.max(STAGE.MIN_WIDTH, 2000));
    const stageLeft = Math.round((worldW - stageWidth) / 2);
    const stageRight = stageLeft + stageWidth;
    const bounds: StageBounds = { stageLeft, stageRight, stageWidth };

    // Create empty grid
    createGrid();

    // Generate fixed layout
    const layout = stage.generateLayout(stageLeft, stageWidth, playerCount);

    // Rasterize + sand draping (same pipeline as procedural)
    rasterizeWireframe(layout);
    generateSandHeightmap(layout);
    rebuildColumnTopCache();

    // Spawn positions on ground segments
    const spawnXs = calculateSpawnPositionsOnGround(layout.groundSegments, playerCount, bounds);

    // Flatten + validate
    flattenSpawnsOnGrid(spawnXs);
    validatePlayability(spawnXs);
    rebuildColumnTopCache();

    console.log(`[TERRAIN] Stage: ${stage.theme.name} (seed: ${resolvedSeed.toString(36).toUpperCase()}, stage: ${stageWidth}px)`);

    return {
      seed: resolvedSeed,
      biome: stageId,
      biomeDisplayName: stage.theme.name,
      feature: null,
      featureDisplayName: null,
      spawnXs,
      stageLeft,
      stageRight,
    };
  }

  // Procedural archetype path (original)
  setWorldWidth(2560); // standard width for procedural archetypes
  // 2. Select archetype (weighted random, or forced by mode)
  const archetype = forcedArchetype
    ? { type: forcedArchetype, displayName: forcedArchetype.charAt(0).toUpperCase() + forcedArchetype.slice(1), weight: 1 }
    : selectArchetype();

  // 3. Calculate stage bounds
  const bounds = calculateStageBoundsForArchetype(archetype.type, playerCount);

  // 4. Create empty grid
  createGrid();

  // 5. Generate wireframe layout
  const layout = generateArchetypeLayout(archetype.type, bounds.stageLeft, bounds.stageWidth, playerCount);

  // 6. Rasterize wireframe to grid (cell = WIREFRAME)
  rasterizeWireframe(layout);

  // 7. Generate sand heightmap over wireframe (cell = SAND)
  // Skip for ascent archetype — vertical worlds use bare wireframe
  if (archetype.type !== 'ascent') {
    generateSandHeightmap(layout);
  }

  // 7b. Rebuild caches now (spawn + validation steps need accurate getGroundY)
  rebuildColumnTopCache();

  // 8. Calculate spawn positions on ground segments
  const spawnXs = calculateSpawnPositionsOnGround(layout.groundSegments, playerCount, bounds);

  // 9. Flatten spawn areas (clear sand above spawns, leave wireframe intact)
  flattenSpawnsOnGrid(spawnXs);

  // 10. Playability validation (clear above spawns)
  validatePlayability(spawnXs);

  // 11. Rebuild caches after validation clearing
  rebuildColumnTopCache();

  console.log(`[TERRAIN] ${archetype.displayName} (seed: ${resolvedSeed.toString(36).toUpperCase()}, stage: ${bounds.stageWidth}px)`);

  return {
    seed: resolvedSeed,
    biome: archetype.type,
    biomeDisplayName: archetype.displayName,
    feature: null,
    featureDisplayName: null,
    spawnXs,
    stageLeft: bounds.stageLeft,
    stageRight: bounds.stageRight,
  };
}

// --- Pipeline helpers ---

/** Stage bounds using archetype width ranges. */
function calculateStageBoundsForArchetype(type: ArchetypeType, playerCount: number): StageBounds {
  const key = type.toUpperCase();
  const widthRange = ARCHETYPE.WIDTH[key];
  if (!widthRange) {
    // Fallback
    return { stageLeft: 280, stageRight: 2280, stageWidth: 2000 };
  }

  const [minW, maxW] = widthRange;
  const playerBonus = STAGE.PLAYER_WIDTH_BONUS * Math.max(0, playerCount - 2);
  const adjustedMin = Math.min(minW + playerBonus, STAGE.MAX_WIDTH);
  const adjustedMax = Math.min(maxW + playerBonus, STAGE.MAX_WIDTH);

  let stageWidth = Math.round(randomRange(adjustedMin, adjustedMax));
  stageWidth = Math.max(STAGE.MIN_WIDTH, Math.min(STAGE.MAX_WIDTH, stageWidth));

  const stageLeft = Math.round((WORLD.VIRTUAL_WIDTH - stageWidth) / 2);
  const stageRight = stageLeft + stageWidth;

  return { stageLeft, stageRight, stageWidth };
}

/** Place spawns on ground segments — distributed across widest/distinct segments. */
function calculateSpawnPositionsOnGround(
  grounds: WireframeGround[],
  playerCount: number,
  bounds: StageBounds,
): number[] {
  if (grounds.length === 0) {
    // Fallback: evenly space across stage
    return calculateSpawnPositions(bounds, playerCount);
  }

  // Sort by width descending (prefer largest islands)
  const sorted = [...grounds].sort((a, b) => b.width - a.width);

  if (playerCount <= 2 && sorted.length >= 2) {
    // Archipelago: one player per island (largest two)
    const x0 = Math.round(sorted[0].x + sorted[0].width * 0.5);
    const x1 = Math.round(sorted[1].x + sorted[1].width * 0.5);
    // If segments have the same center (stacked vertically, e.g. ascent mode),
    // fall through to spread players on the widest segment instead
    if (Math.abs(x0 - x1) > 100) {
      return [x0, x1];
    }
  }

  // Single ground or 2+ players: evenly space along the single largest segment
  const seg = sorted[0];
  const margin = seg.width * STAGE.SPAWN_MARGIN_FRAC;
  const usable = seg.width - 2 * margin;

  if (playerCount <= 1) {
    return [Math.round(seg.x + seg.width / 2)];
  }

  const spawnXs: number[] = [];
  for (let i = 0; i < playerCount; i++) {
    const t = i / (playerCount - 1);
    spawnXs.push(Math.round(seg.x + margin + t * usable));
  }
  return spawnXs;
}

/** Clear ALL terrain (sand + wireframe) above spawn positions for headroom. */
function flattenSpawnsOnGrid(spawnXs: number[]): void {
  const grid = getGrid();
  if (!grid) return;

  const clearRadius = TERRAIN_GEN.SPAWN_CLEAR_RADIUS;
  const clearHeight = TERRAIN_GEN.SPAWN_CLEAR_HEIGHT;

  for (const sx of spawnXs) {
    const gx = worldToGridX(sx);
    const groundY = getGroundY(sx);
    const groundGy = worldToGridY(groundY);

    for (let dy = 1; dy <= clearHeight; dy++) {
      const gy = groundGy - dy;
      if (gy < 0) break;
      for (let dx = -clearRadius; dx <= clearRadius; dx++) {
        const cx = gx + dx;
        if (cx >= 0 && cx < TERRAIN.GRID_WIDTH) {
          // Clear all terrain above spawn so player isn't trapped in wireframe
          grid[gy * TERRAIN.GRID_WIDTH + cx] = 0;
        }
      }
    }
  }
}

